// store/forms/delForm/projects/actions.js

import { JsaV1 } from "../../../../models/Jsa/JsaV1";
import { JsaV2 } from "../../../../models/Jsa/JsaV2";
import { WasteDisposal } from "../../../../models/WasteDisposal/WasteDisposal";

const isEqualJson = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

export const makeProjectActions = ({ getDraft, bump, setProjectUi }) => ({
  clearAllProjectInfo(emitPatch) {
    const draft = getDraft();
    draft.projectInfo.clearProjectInfo();
    // Creating the patch to emit to the server
    const patch = draft.projectInfo.toPatch({
      projectNumber: null,
      phaseId: null,
      taskId: null,
      projectManagerId: null,
      invoiceManagerId: null,
      date: null,
    });
    emitPatch(patch);
    setProjectUi({}); // reset project UI subset if needed
    bump();
  },

  toggleNoProjectNumberCheckbox(checked, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setNoProjectNumberChecked(checked);
    // When switching to "No Project Number" mode, clear any stale project data
    // from the database so backend validation doesn't see an orphaned project_number.
    if (checked && draft.projectInfo.projectNumber) {
      draft.projectInfo.clearProjectInfo();
      const patch = draft.projectInfo.toPatch({
        projectNumber: null,
        phaseId: null,
        taskId: null,
        projectManagerId: null,
        invoiceManagerId: null,
      });
      if (emitPatch) emitPatch(patch);
    }
    setProjectUi({});
    bump();
  },

  projectDescriptionChange(value, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setProjectDescription(value);
    const patch = draft.projectInfo.toPatch({
      projectDescription: value,
    });
    // The projectDescription is a text field, we only want to send on emitPatch for onBlur events.
    if (emitPatch) {
      emitPatch(patch);
    }
    bump();
  },

  setSelectedProject(project, employees, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setProjectInfo(project);
    draft.projectInfo.setProjectManager(employees, project);
    draft.projectInfo.setInvoiceManager(employees, project);
    draft.projectInfo.setSelectedPhase(null);
    draft.projectInfo.setSelectedTask(null);
    draft.projectInfo.projectLabel = project?.label || "";
    // Seeing the Client information if it is null or empty
    if (project?.client_name) {
      draft.jsa.jsaBasicInfo.setJsaClientName(project.client_name);
      draft.wasteDisposal.basicInfo.updateTextAttribute(
        "clientName",
        project.client_name
      );
    }
    const patch = draft.projectInfo.toPatch({
      projectNumber: project?.value,
      projectManagerId: draft.projectInfo.projectManager.id,
      invoiceManagerId: draft.projectInfo.invoiceManager.id,
      // Nullify the task and phase fields
      taskId: draft.projectInfo.taskId,
      phaseId: draft.projectInfo.phaseId,
    });
    emitPatch(patch);

    // Update JSA and Waste Disposal client names in the database via websocket
    if (project?.client_name) {
      // JSA client name patch - routed through del_service to jsa_service
      const jsaClientPatch = {
        patch_type: "jsa",
        jsa_data: draft.jsa.jsaBasicInfo.toPatch({
          jsaClientName: project.client_name,
        }),
      };
      emitPatch(jsaClientPatch);

      // Waste Disposal client name patch - routed through del_service to waste_disposal_service
      const wdBasicPatch = draft.wasteDisposal.basicInfo.toPatch({
        clientName: project.client_name,
      });
      const wasteDisposalClientPatch = {
        patch_type: "waste_disposal",
        waste_disposal_data: {
          ...wdBasicPatch,
          del_id: draft.id,
        },
      };
      emitPatch(wasteDisposalClientPatch);
    }

    bump();
  },

  setSelectedPhase(phase, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setSelectedPhase(phase);
    draft.projectInfo.setSelectedTask(null);
    draft.projectInfo.setAvailableTasks(phase);
    setProjectUi({ showPhaseDropdown: false });
    const patch = draft.projectInfo.toPatch({
      projectNumber: draft.projectInfo.projectNumber,
      phaseId: draft.projectInfo.phaseId,
      taskId: draft.projectInfo.taskId,
    });
    emitPatch(patch);
    bump();
  },

  setSelectedTask(task, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setSelectedTask(task);
    setProjectUi({ showTaskDropdown: false });
    const patch = draft.projectInfo.toPatch({
      projectNumber: draft.projectInfo.projectNumber,
      phaseId: draft.projectInfo.phaseId,
      taskId: draft.projectInfo.taskId,
    });
    emitPatch(patch);
    bump();
  },

  setSelectedInvoiceManager(manager, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setInvoiceManagerSearchInput(manager);
    const patch = draft.projectInfo.toPatch({
      invoiceManagerId: draft.projectInfo.invoiceManager.id,
    });
    emitPatch(patch);
    bump();
  },

  setProjectDate(date, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setProjectDate(date);
    const patch = draft.projectInfo.toPatch({
      date: draft.projectInfo.date,
    });
    emitPatch(patch);
    bump();
  },

  setShowJsaTab(checked, emitPatch) {
    const draft = getDraft();
    // If user is marking JSA not required and JSA contains data, confirm destructive action
    if (checked) {
      const emptyJsa =
        draft.projectInfo.jsaVersion === 2 ? new JsaV2() : new JsaV1();
      const hasData = !isEqualJson(draft.jsa.toJson(), emptyJsa.toJson());
      if (hasData) {
        const ok = window.confirm(
          "Marking JSA complete/not required will clear existing JSA data. Continue?"
        );
        if (!ok) return;
        // Clear JSA data
        draft.jsa = emptyJsa;
      }
    }
    draft.projectInfo.setJsaNotRequired(checked);
    setProjectUi((prev) => ({ showJsaTab: !checked }));
    const patch = draft.projectInfo.toPatch({
      jsaNotRequired: checked,
      jsaAlreadyCompleted: checked,
    });
    emitPatch(patch);
    bump();
  },

  setShowWasteDisposalTab(checked, emitPatch) {
    const draft = getDraft();
    // If user is marking Waste Disposal not required and WD contains data, confirm destructive action
    if (checked) {
      const emptyWd = new WasteDisposal();
      const hasData = !isEqualJson(
        draft.wasteDisposal.toJson(),
        emptyWd.toJson()
      );
      if (hasData) {
        const ok = window.confirm(
          "Marking Waste Disposal complete/not required will clear existing Waste Disposal data. Continue?"
        );
        if (!ok) return;
        // Clear Waste Disposal data
        draft.wasteDisposal = emptyWd;
      }
    }
    draft.projectInfo.setWasteDisposalNotRequired(checked);
    setProjectUi((prev) => ({ showWasteDisposalTab: !checked }));
    const patch = draft.projectInfo.toPatch({
      wasteDisposalNotRequired: checked,
      wasteDisposalAlreadyCompleted: checked,
    });
    emitPatch(patch);
    bump();
  },

  setShowClientSignaturesTab(checked, emitPatch) {
    const draft = getDraft();
    draft.projectInfo.setClientSignaturesRequired(checked);
    setProjectUi((prev) => ({ showClientSignaturesTab: checked }));
    const patch = draft.projectInfo.toPatch({
      clientSignaturesRequired: checked,
    });
    emitPatch(patch);
    bump();
  },

  handleBroadcast(patch, applyPatchToObject) {
    const draft = getDraft();
    if (!draft) return;

    // Step 1: Apply simple scalar fields via camelCase setter/assignment.
    // This handles: projectNumber, phaseId, taskId, phaseName, taskName,
    // projectName, clientName, and boolean flags.
    // Fields that are silently skipped (handled manually in Step 2/3):
    //   del_date -> delDate (property is 'date'),
    //   project_name_description -> projectNameDescription (property is 'projectDescription'),
    //   phase_list -> phaseList (no property),
    //   manager IDs/names (nested objects).
    applyPatchToObject(draft.projectInfo, patch);

    // Step 2: Handle fields that applyPatchToObject cannot reach.
    // del_date -> delDate, but DelProject stores it as 'date', so it's skipped.
    if (patch.del_date !== undefined) {
      draft.projectInfo.date = patch.del_date || "";
    }

    // project_name_description -> projectNameDescription by applyPatchToObject,
    // but DelProject stores it as 'projectDescription' with setter 'setProjectDescription'.
    // Handle manually due to naming mismatch (same class of issue as del_date and managers).
    if (patch.project_name_description !== undefined) {
      draft.projectInfo.projectDescription = patch.project_name_description || "";
      // Derive checkbox state from description content.
      // Mirrors constructor logic: this.noProjectNumberChecked = data.project_name_description || false
      draft.projectInfo.noProjectNumberChecked = !!patch.project_name_description;
    }

    // Nested manager objects (projectManager.id/name, invoiceManager.id/name)
    if (patch.project_manager_id !== undefined) {
      draft.projectInfo.projectManager.id = patch.project_manager_id || null;
    }
    if (patch.project_manager_name !== undefined) {
      draft.projectInfo.projectManager.name =
        patch.project_manager_name || "";
    }
    if (patch.invoice_manager_id !== undefined) {
      draft.projectInfo.invoiceManager.id = patch.invoice_manager_id || null;
    }
    if (patch.invoice_manager_name !== undefined) {
      draft.projectInfo.invoiceManager.name =
        patch.invoice_manager_name || "";
    }

    // Step 3: Handle phase_list for dropdown reconstruction.
    // The backend sends phase_list (with nested task_lists) on project,
    // phase, and task changes so that listening clients can rebuild their
    // dropdown options in real-time.
    if (patch.phase_list !== undefined) {
      draft.projectInfo.updateAvailablePhasesFromBroadcast(patch.phase_list);

      // Safety net: re-derive phaseName/taskName from lists if the backend
      // enrichment didn't set them (should be rare since the backend sends them)
      if (draft.projectInfo.phaseId && !draft.projectInfo.phaseName) {
        const matchedPhase = draft.projectInfo.availablePhases.find(
          (p) => p.value === draft.projectInfo.phaseId
        );
        draft.projectInfo.phaseName = matchedPhase?.label || "";
      }
      if (draft.projectInfo.taskId && !draft.projectInfo.taskName) {
        const matchedTask = draft.projectInfo.availableTasks.find(
          (t) => t.value === draft.projectInfo.taskId
        );
        draft.projectInfo.taskName = matchedTask?.label || "";
      }
    }

    // Step 4: Reconstruct projectLabel.
    // Prefer the pre-built label from the backend (from Redis cache).
    // Fall back to reconstructing from component parts.
    if (patch.label !== undefined) {
      draft.projectInfo.projectLabel = patch.label || "";
    } else if (
      patch.project_number !== undefined ||
      patch.project_name !== undefined ||
      patch.client_name !== undefined
    ) {
      const pn = draft.projectInfo.projectNumber || "";
      const pName = draft.projectInfo.projectName || "";
      const cName = draft.projectInfo.clientName || "";
      draft.projectInfo.projectLabel =
        pn && pName ? `${pn} - ${pName} - ${cName}` : "";
    }

    // Step 5: Sync tab visibility UI state with model boolean flags.
    // When jsa_not_required or jsa_already_completed changes via broadcast,
    // the projectUI.showJsaTab must be updated to match.
    const uiPatch = {};
    if (
      patch.jsa_not_required !== undefined ||
      patch.jsa_already_completed !== undefined
    ) {
      uiPatch.showJsaTab =
        !draft.projectInfo.jsaNotRequired &&
        !draft.projectInfo.jsaAlreadyCompleted;
    }
    if (
      patch.waste_disposal_not_required !== undefined ||
      patch.waste_disposal_already_completed !== undefined
    ) {
      uiPatch.showWasteDisposalTab =
        !draft.projectInfo.wasteDisposalNotRequired &&
        !draft.projectInfo.wasteDisposalAlreadyCompleted;
    }
    if (patch.client_signatures_required !== undefined) {
      uiPatch.showClientSignaturesTab =
        !!draft.projectInfo.clientSignaturesRequired;
    }
    if (Object.keys(uiPatch).length > 0) {
      setProjectUi((prev) => uiPatch);
    }

    bump();
  },
});
